import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../components/icon';
import SelectField from '../components/select-field';
import TextField from '../components/text-field';
import { listUsers, resendInvitation } from '../api/panel-api';
import { formatDateTime, formatPhone, fullName, panelRoleLabel, panelRoles, serviceRoleLabel } from '../data/panel';

const emptyFilters = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  status: '',
  role: '',
};

function toQueryFilters(filters) {
  return {
    firstName: filters.firstName,
    lastName: filters.lastName,
    phone: filters.phone,
    email: filters.email,
    role: filters.role,
    deleted: filters.status === 'deleted',
  };
}

function presentUser(item) {
  const stamp = formatDateTime(item.last_login_at);
  return {
    id: item.id,
    firstName: item.first_name,
    lastName: item.last_name,
    email: item.email,
    position: item.job_title,
    role: panelRoleLabel[item.role] || item.role,
    serviceRole: item.cs_role === 'rks' ? serviceRoleLabel.rks : '',
    status: item.is_deleted ? 'deleted' : item.is_invited ? 'invited' : 'active',
    date: stamp.date,
    time: stamp.time,
  };
}

function UsersPage({ onOpen, onCreate }) {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [draft, setDraft] = useState(emptyFilters);
  const [applied, setApplied] = useState(emptyFilters);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortDir, setSortDir] = useState('asc');
  const [pageSize, setPageSize] = useState(30);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState(null);
  const [comment, setComment] = useState('');
  const [pending, setPending] = useState(false);
  const [dialogError, setDialogError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const appliedCount = Object.values(applied).filter(Boolean).length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listUsers({
      page,
      perPage: pageSize,
      order: sortDir,
      filters: toQueryFilters(applied),
    })
      .then((data) => {
        if (cancelled) return;
        setUsers((data.items || []).map(presentUser));
        setTotal(data.total || 0);
        setError('');
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [applied, page, pageSize, sortDir, reloadKey]);

  function openFilters() {
    setDraft(applied);
    setFiltersOpen(true);
  }

  function closeFilters() {
    setDraft(applied);
    setFiltersOpen(false);
  }

  function openDialog(type, user) {
    setComment('');
    setDialogError('');
    setDialog({ type, user });
  }

  async function confirmDialog() {
    if (!dialog || dialog.type !== 'invite') return;
    setPending(true);
    setDialogError('');
    try {
      await resendInvitation(dialog.user.id);
      setDialog(null);
      setReloadKey((value) => value + 1);
    } catch (inviteError) {
      setDialogError(inviteError.message);
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="content content--panel" aria-label="Пользователи" aria-busy={loading}>
      <div className="content__scroll">
      <header className="page-head">
        <h1 className="page-title">Пользователи <span className="counter">{total}</span></h1>
        <div className="page-actions">
          <button type="button" className="btn btn--secondary" onClick={onCreate}>
            <Icon name="plus" size={16} />
            Добавить пользователя
          </button>
          <button type="button" className="btn" aria-pressed={filtersOpen} onClick={openFilters}>
            <Icon name="filter" size={16} tone="inherit" />
            Фильтры
            {appliedCount ? <span className="btn__count">{appliedCount}</span> : null}
          </button>
        </div>
      </header>
      <div className="users-table">
      <table className="table">
        <thead>
          <tr>
            <th style={{ width: 240 }}>
              <button type="button" className={sortDir === 'desc' ? 'sort is-desc' : 'sort'} onClick={() => { setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc')); setPage(1); }}>
                Имя и фамилия
                <Icon name="arrow-up" size={14} />
              </button>
            </th>
            <th style={{ width: 74 }}>Статус</th>
            <th>Электронная почта</th>
            <th>Должность</th>
            <th>Роль в панели</th>
            <th style={{ width: 110 }}>Последний вход</th>
            <th style={{ width: 92 }} />
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr
              key={user.id}
              className="table__row"
              tabIndex={0}
              onClick={() => onOpen(user.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onOpen(user.id);
                }
              }}
            >
              <td className="table__name" title={fullName(user)}>{fullName(user)}</td>
              <td>
                <Icon
                  name={user.status === 'invited' ? 'clock' : user.status === 'deleted' ? 'x' : 'status-active'}
                  size={20}
                />
              </td>
              <td>{user.email}</td>
              <td>{user.position}</td>
              <td>
                {user.role}
                {user.serviceRole ? <span className="table__role-note">{user.serviceRole}</span> : null}
              </td>
              <td>
                {user.date ? user.date : '—'}
                {user.time ? <span className="table__time">{user.time}</span> : null}
              </td>
              <td>
                <button
                  type="button"
                  className="icon-button"
                  aria-label={user.status === 'invited' ? 'Отправить повторно' : 'Открыть'}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (user.status === 'invited') openDialog('invite', user);
                    else onOpen(user.id);
                  }}
                >
                  <Icon name={user.status === 'invited' ? 'send' : 'pencil'} size={18} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {error ? (
        <div className="empty users-empty"><p>{error}</p></div>
      ) : null}
      {!error && !loading && users.length === 0 ? (
        <div className="empty users-empty">
          <div className="empty__icon"><Icon name="filter" size={24} tone="inherit" /></div>
          <div className="empty__copy">
            <p className="empty__title">Под такие фильтры пользователей нет</p>
            <p className="empty__lead">Смягчите условия или сбросьте фильтры</p>
          </div>
          <button type="button" className="btn btn--secondary" onClick={() => { setDraft(emptyFilters); setApplied(emptyFilters); setPage(1); }}>Сбросить фильтры</button>
        </div>
      ) : null}
      </div>
      {users.length ? (
      <footer className="pager">
        <label className="pager__size">
          Показывать по
          <select value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPage(1); }}>
            <option value={30}>30</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </label>
        <div className="pager__nav">
          <span>{total ? `${from}–${to} из ${total}` : '0 из 0'}</span>
          <button type="button" className="icon-button" aria-label="Предыдущая страница" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>
            <Icon name="chevron-left" size={16} />
          </button>
          <span className="counter counter--page">{page}</span>
          <button type="button" className="icon-button" aria-label="Следующая страница" disabled={page >= pageCount} onClick={() => setPage((value) => value + 1)}>
            <Icon name="chevron-right" size={16} />
          </button>
        </div>
      </footer>
      ) : null}
      </div>
      {filtersOpen ? createPortal(
        <>
          <button type="button" className="filter-shade" aria-label="Закрыть фильтры" onClick={closeFilters} />
          <aside className="filter-panel" aria-label="Фильтры">
            <div className="filter-panel__head">
              <h2>Фильтры</h2>
              <button type="button" className="icon-button" aria-label="Закрыть" onClick={closeFilters}>
                <Icon name="x" size={18} />
              </button>
            </div>
            <div className="filter-panel__body">
              <TextField label="Имя" value={draft.firstName} required={false} trailing="clear" onChange={(value) => setDraft({ ...draft, firstName: value })} />
              <TextField label="Фамилия" value={draft.lastName} required={false} trailing="clear" onChange={(value) => setDraft({ ...draft, lastName: value })} />
              <TextField label="Телефон" value={draft.phone} required={false} trailing="clear" autoComplete="off" onChange={(value) => setDraft({ ...draft, phone: formatPhone(value) })} />
              <TextField label="Электронная почта" type="email" value={draft.email} required={false} trailing="clear" autoComplete="off" onChange={(value) => setDraft({ ...draft, email: value })} />
              <SelectField
                label="Статус"
                required={false}
                clearable
                value={draft.status}
                onChange={(value) => setDraft({ ...draft, status: value })}
                options={[
                  { value: 'active', label: 'Активные' },
                  { value: 'deleted', label: 'Удалённые' },
                ]}
              />
              <SelectField
                label="Роль в панели"
                required={false}
                clearable
                value={draft.role}
                onChange={(value) => setDraft({ ...draft, role: value })}
                options={panelRoles}
              />
            </div>
            <div className="filter-panel__foot">
              <button type="button" className="btn" onClick={() => { setApplied(draft); setPage(1); setFiltersOpen(false); }}>
                Применить
              </button>
            </div>
          </aside>
        </>,
        document.body,
      ) : null}
      {dialog ? (
        <Dialog
          dialog={dialog}
          comment={comment}
          onComment={setComment}
          pending={pending}
          error={dialogError}
          onClose={() => setDialog(null)}
          onConfirm={confirmDialog}
        />
      ) : null}
    </section>
  );
}

function Dialog({ dialog, comment, onComment, onClose, onConfirm, pending, error }) {
  const { type, user } = dialog;
  const name = fullName(user);
  if (type === 'invite') {
    return (
      <Modal title="Отправить приглашение повторно" icon="send" onClose={onClose} confirm="Отправить" onConfirm={onConfirm} pending={pending} error={error}>
        <Who name={name} detail={user.email} />
        <Note tone="info">Прежняя ссылка перестанет работать. Новая будет действительна в течение суток. Пароль пользователь задаёт самостоятельно.</Note>
      </Modal>
    );
  }
  if (type === 'reset') {
    return (
      <Modal title="Сбросить пароль" icon="key" onClose={onClose} confirm="Сбросить" onConfirm={onConfirm} pending={pending} error={error}>
        <Who name={name} detail={user.email} />
        <Note tone="info">Текущий пароль перестанет работать сразу и его сессия будет завершена. Пользователю уйдёт уведомление о сбросе, новый пароль он задаст сам.</Note>
      </Modal>
    );
  }
  if (type === 'delete-blocked') {
    return (
      <Modal title="Сначала увольте менеджера" icon="alert" onClose={onClose} cancel="Закрыть" confirm="Перейти в «Менеджеры»" onConfirm={onConfirm}>
        <Who name={name} detail={user.blockDetail || user.email} />
        <Note tone="danger">Для пользователя назначены клиенты, поэтому удалить его нельзя. Сначала увольте менеджера в разделе «Менеджеры» и передайте его базу, после этого удаление станет доступно.</Note>
      </Modal>
    );
  }
  if (type === 'role') {
    return (
      <Modal title="Сначала передайте клиентскую базу" icon="alert" onClose={onClose} cancel="Закрыть" confirm="Перейти в «Менеджеры»" onConfirm={onConfirm}>
        <Who name={name} detail={user.blockDetail || user.email} />
        <Note tone="danger">Для пользователя назначены клиенты, поэтому поменять его роль нельзя. Сначала передайте клиентов другому сотруднику КС в разделе «Менеджеры», после этого смена роли станет доступна.</Note>
      </Modal>
    );
  }
  if (type === 'restore') {
    return (
      <Modal title="Восстановить пользователя" icon="check" onClose={onClose} confirm="Восстановить" onConfirm={onConfirm} pending={pending} error={error}>
        <Who name={name} detail={user.email} />
        <textarea className="comment" placeholder="Комментарий" value={comment} onChange={(event) => onComment(event.target.value)} />
        <Note tone="info">Вернётся прежняя учётная запись. Пароль и сессии сбросятся, доступы к разделам нужно выдать заново.</Note>
      </Modal>
    );
  }
  return (
    <Modal title="Удалить пользователя" icon="trash" onClose={onClose} confirm="Удалить" danger onConfirm={onConfirm} pending={pending} error={error}>
      <Who name={name} detail={user.email} />
      <textarea className="comment" placeholder="Комментарий" value={comment} onChange={(event) => onComment(event.target.value)} />
      <Note tone="warning">Карточка останется доступной для чтения, ссылки на пользователя в журнале действий и комментариях сохранятся. Войти в панель он больше не сможет.</Note>
    </Modal>
  );
}

function Modal({ title, icon, onClose, confirm, cancel = 'Отмена', danger = false, onConfirm, pending, error, children }) {
  return (
    <div className="overlay">
      <div className="dialog" role="dialog" aria-modal="true">
        <div className="dialog__head">
          <h2><Icon name={icon} size={22} />{title}</h2>
          <button type="button" className="icon-button" aria-label="Закрыть" onClick={onClose}><Icon name="x" size={18} /></button>
        </div>
        <div className="dialog__body">
          {children}
          {error ? <p className="empty">{error}</p> : null}
        </div>
        <div className="dialog__footer">
          <button type="button" className="btn btn--secondary btn--small" onClick={onClose}>{cancel}</button>
          <button type="button" className={danger ? 'btn btn--danger btn--small' : 'btn btn--small'} disabled={pending} onClick={onConfirm || onClose}>{confirm}</button>
        </div>
      </div>
    </div>
  );
}

function Who({ name, detail }) {
  return (
    <div className="who">
      <strong>{name}</strong>
      <span>{detail}</span>
    </div>
  );
}

function Note({ tone, children }) {
  return (
    <div className={`note note--${tone}`}>
      <Icon name={tone === 'warning' || tone === 'danger' ? 'alert' : 'info'} size={20} />
      <p>{children}</p>
    </div>
  );
}

export { Dialog };
export default UsersPage;
